// Library Methods
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import OperatingManualContent from "../OperatingManualContent/OperatingManualContent";
import GradientHeader from "src/components/shared/displays/GradientHeader/GradientHeader";

type SystemDescriptionType = {
  title: string;
};

const SystemDescription = ({ title }: SystemDescriptionType) => {
  const { t } = useTranslation("operating-manual");

  return (
    <OperatingManualContent title={title}>
      <Typography>
        {t(
          "The RetinaLogik RVF200 solution works with a virtual reality (VR) device. The solution involves two components:"
        )}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          my: "20px",
          mx: "20px",
        }}
      >
        <Typography>
          1.{" "}
          <span style={{ color: "#0066CC", fontWeight: 600 }}>
            {t("The RetinaLogik VR Application")}:
          </span>{" "}
          {t("The VR application allows for performance of the visual exam.")}
        </Typography>
        <Typography>
          2.{" "}
          <span style={{ color: "#0066CC", fontWeight: 600 }}>
            {t("The RetinaLogik Browser Based Portal")}:
          </span>{" "}
          {t(
            "The web portal is a website for management of patient exams. This is where users can create exams for patients and view their results. The web portal can be accessed at:"
          )}{" "}
          <Link
            to="http://portal.retinalogik.ca/"
            target="_blank"
            style={{ color: "#0066CC", textDecoration: "underline" }}
          >
            portal.retinalogik.ca
          </Link>
        </Typography>
      </Box>

      <Typography fontWeight={600}>
        {t(
          "The RVF200 software comes preloaded on a virtual reality headset. Necessary accessories are also provided to operate the VR unit. This operating manual includes instructions on how to use the virtual reality headset as part of the RVF200 solution."
        )}
      </Typography>

      <Box
        id="intended-use"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <GradientHeader
          header={`1.1 ${t("Intended Use & Indications for Use")}`}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            mx: "20px",
          }}
        >
          <Typography fontWeight={600}>{t("Intended Use")}</Typography>
          <Typography>
            {t(
              "RVF200 is an ophthalmic device used to screen, monitor, and assist in the detection and progression monitoring of visual field defects. It is indicated for use by trained technicians, optometrists and ophthalmologists or equivalent."
            )}
          </Typography>

          <Typography fontWeight={600}>{t("Indications for Use")}</Typography>
          <Typography>
            {t(
              "The RVF device family is intended to identify visual field defects for the purposes of screening, monitoring and assisting in the diagnosis and management of visual defects and diseases ranging from color blindness to Glaucoma, and related neurological disorders."
            )}
          </Typography>
          <Typography>
            {t(
              "RVF test results alone cannot be considered a diagnosis and cannot be used as a sole screening tool. Optometrists, ophthalmologists (or equivalent) should review all test results, including statistical measures, to determine their clinical significance."
            )}
          </Typography>
          <Typography>
            {t(
              "RVF is not recommended for testing of children below age seven (7)."
            )}
          </Typography>
        </Box>
      </Box>

      <Box
        id="usage"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <GradientHeader header={`1.2 ${t("Usage")}`} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            mx: "20px",
          }}
        >
          <Typography>
            {t("The operator of the RVF200 should be one of the following:")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              mx: "20px",
            }}
          >
            <Typography>• {t("Optician")}</Typography>
            <Typography>• {t("Nurse")}</Typography>
            <Typography>• {t("Technician")}</Typography>
            <Typography>• {t("Ophthalmologist")}</Typography>
            <Typography>• {t("Optometrist")}</Typography>
            <Typography>• {t("Clinical Assistant")}</Typography>
          </Box>
        </Box>
      </Box>

      <Box
        id="results"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <GradientHeader header={`1.3 ${t("Results")}`} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            mx: "20px",
          }}
        >
          <Typography>
            {t(
              "Visual field results can be interpreted by a medical doctor, ophthalmologist, optometrist or equivalent."
            )}
          </Typography>
        </Box>
      </Box>
    </OperatingManualContent>
  );
};

export default SystemDescription;
